import z from "zod";

import { ROLE_NAME } from "@/constants/roles";
import { postedAvailabilityWindowResponseSchema } from "@/schemas/profile";

import { apiRequest } from "./apiUtils";

/* ── This week (SPEC §3.2) ─────────────────────────────────────────────────── */

export const WeekShiftSchema = z.object({
  shift_id: z.number(),
  role_name: z.string().nullable().optional(),
  start_at: z.string().nullable().optional(),
  end_at: z.string().nullable().optional(),
  status: z.enum(["filled", "open"]),
  worker_id: z.string().nullable().optional(),
  worker_name: z.string().nullable().optional(),
});
export type WeekShift = z.infer<typeof WeekShiftSchema>;

export const WeekDaySchema = z.object({
  date: z.string(),
  shifts: z.array(WeekShiftSchema).default([]),
});
export type WeekDay = z.infer<typeof WeekDaySchema>;

const WeekResponseSchema = z.object({
  success: z.boolean().default(true),
  start: z.string(),
  end: z.string(),
  totals: z.object({
    shifts: z.number().default(0),
    filled: z.number().default(0),
    open: z.number().default(0),
  }),
  days: z.array(WeekDaySchema).default([]),
});
export type DashboardWeek = z.infer<typeof WeekResponseSchema>;

/** Fetch a Monday-anchored week of shifts. `start` is an ISO date (yyyy-MM-dd). */
export async function getDashboardWeek(start?: string): Promise<DashboardWeek> {
  const qs = start ? `?start=${encodeURIComponent(start)}` : "";
  const data = await apiRequest<unknown>(`business/dashboard/week${qs}`, {
    method: "GET",
  });
  return WeekResponseSchema.parse(data);
}

/* ── Your Roster (SPEC §3.4) ───────────────────────────────────────────────── */

export const RosterFilters = [
  "all",
  "starred",
  "available_week",
  "inactive_30d",
] as const;
export type RosterFilter = (typeof RosterFilters)[number];

function roleLabels(roles: Array<string>): Array<string> {
  const seen = new Set<string>();
  const out: Array<string> = [];
  for (const r of roles) {
    const label = r in ROLE_NAME ? ROLE_NAME[r].label : r;
    const k = label.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(label);
  }
  return out;
}

export const RosterItemSchema = z.object({
  worker_id: z.string(),
  name: z.string().nullable().optional(),
  initials: z.string().nullable().optional(),
  // Shift roles arrive as display names ("Line cook"); roles an owner recorded via
  // "Add more workers" arrive as role keys ("line_cook"). Show labels, without repeats.
  roles: z.array(z.string()).default([]).transform(roleLabels),
  last_worked_at: z.string().nullable().optional(),
  shift_count: z.number().default(0),
  // Published 1-5 rating (RatingService/Bayesian). Null until the worker reaches
  // MIN_RATING_RESPONSES; StarRating renders "New" in that case.
  rating: z.number().nullable().optional(),
  rating_count: z.number().nullable().optional(),
  next_shift: z
    .object({ id: z.number().nullable().optional(), start_at: z.string().nullable().optional() })
    .nullable()
    .optional(),
  starred: z.boolean().default(false),
  is_new: z.boolean().default(false),
  // Declared availability windows (used by the Availability column; the UI keeps
  // only the next 14 days). Also attached to employer worker-search rows.
  posted_availability: z
    .array(postedAvailabilityWindowResponseSchema)
    .nullable()
    .optional(),
  // True on Search rows for workers who have completed a shift for this employer.
  // Always false/absent on the roster itself (everyone there has worked here).
  worked_before: z.boolean().nullable().optional(),
});
export type RosterItem = z.infer<typeof RosterItemSchema>;

const RosterResponseSchema = z.object({
  success: z.boolean().default(true),
  total: z.number().default(0),
  starred_count: z.number().default(0),
  items: z.array(RosterItemSchema).default([]),
  next_cursor: z.string().nullable().optional(),
});
export type RosterResponse = z.infer<typeof RosterResponseSchema>;

/** Column sort + name/role text filter, applied server-side (same rules as worker search). */
export type RosterQuery = { sort?: string | null; dir?: "asc" | "desc" | null; q?: string | null };

export async function getRoster(
  filter: RosterFilter = "all",
  limit = 7,
  cursor?: string | null,
  query: RosterQuery = {},
): Promise<RosterResponse> {
  const params = new URLSearchParams({ filter, limit: String(limit) });
  if (cursor) params.set("cursor", cursor);
  if (query.sort) params.set("sort", query.sort);
  if (query.dir) params.set("dir", query.dir);
  if (query.q) params.set("q", query.q);
  const data = await apiRequest<unknown>(`business/roster?${params.toString()}`, {
    method: "GET",
  });
  return RosterResponseSchema.parse(data);
}

/* ── Needs attention (SPEC §3.1) ───────────────────────────────────────────── */

export const AttentionItemSchema = z.object({
  id: z.string(),
  type: z.enum(["unfilled_soon", "shift_cancelled", "no_signups", "new_application", "new_worker_signup", "rate_completed", "running_late", "new_message"]),
  severity: z.enum(["urgent", "normal"]).default("normal"),
  title: z.string().nullable().optional(),
  subtext: z.string().nullable().optional(),
  shift_id: z.number().nullable().optional(),
  worker_id: z.string().nullable().optional(),
  primary_action: z
    .object({
      label: z.string().nullable().optional(),
      kind: z.enum(["find_cover", "adjust_pay", "find_workers", "view_profile", "rate", "view_shift", "message_worker", "view_message"]),
      target_id: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
});
export type AttentionItem = z.infer<typeof AttentionItemSchema>;

const AttentionResponseSchema = z.object({
  success: z.boolean().default(true),
  items: z.array(AttentionItemSchema).default([]),
});

export async function getAttention(): Promise<Array<AttentionItem>> {
  const data = await apiRequest<unknown>("business/dashboard/attention", { method: "GET" });
  return AttentionResponseSchema.parse(data).items;
}

/* ── Activity feed (SPEC §3.3) ─────────────────────────────────────────────── */

export const EventFilters = ["all", "signup", "confirmed", "cancellation", "posted", "completed"] as const;
export type EventFilter = (typeof EventFilters)[number];

export const EventItemSchema = z.object({
  id: z.string(),
  type: z.enum(["applied", "signup", "confirmed", "cancellation", "running_late", "posted", "completed", "rated"]),
  occurred_at: z.string().nullable().optional(),
  actor: z
    .object({
      id: z.string().nullable().optional(),
      name: z.string().nullable().optional(),
      starred: z.boolean().default(false),
      prior_shift_count: z.number().default(0),
    })
    .nullable()
    .optional(),
  shift: z
    .object({
      id: z.number().nullable().optional(),
      role_name: z.string().nullable().optional(),
      start_at: z.string().nullable().optional(),
      end_at: z.string().nullable().optional(),
      status: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  note: z.string().nullable().optional(),
});
export type EventItem = z.infer<typeof EventItemSchema>;

const EventsResponseSchema = z.object({
  success: z.boolean().default(true),
  unread_count: z.number().default(0),
  items: z.array(EventItemSchema).default([]),
  next_cursor: z.string().nullable().optional(),
});
export type EventsResponse = z.infer<typeof EventsResponseSchema>;

export async function getEvents(
  filter: EventFilter = "all",
  limit = 7,
  cursor?: string | null,
): Promise<EventsResponse> {
  const qs = cursor ? `&cursor=${encodeURIComponent(cursor)}` : "";
  const data = await apiRequest<unknown>(
    `business/events?filter=${filter}&limit=${limit}${qs}`,
    { method: "GET" },
  );
  return EventsResponseSchema.parse(data);
}

export async function markEventsRead(): Promise<void> {
  await apiRequest<unknown>("business/events/mark-read", { method: "POST" });
}

/** Star/unstar a roster worker (PUT .../roster/{workerId}/star). */
export async function setRosterStar(
  workerId: string,
  starred: boolean,
): Promise<boolean> {
  const data = await apiRequest<unknown>(
    `business/roster/${encodeURIComponent(workerId)}/star`,
    { method: "PUT", body: JSON.stringify({ starred }) },
  );
  return z
    .object({ success: z.boolean().default(true), starred: z.boolean() })
    .parse(data).starred;
}

/** Record that the employer viewed a worker's profile (POST .../roster/{workerId}/seen).
 *  Dismisses that worker's "new_worker_signup" Needs-attention item. */
export async function markWorkerSeen(workerId: string): Promise<void> {
  await apiRequest<unknown>(
    `business/roster/${encodeURIComponent(workerId)}/seen`,
    { method: "POST" },
  );
}

/** Acknowledge (dismiss) a "running_late" Needs-attention item for an assignment
 *  (POST .../dashboard/attention/running-late/{assignmentId}/ack). */
export async function acknowledgeRunningLate(
  assignmentId: string | number,
): Promise<void> {
  await apiRequest<unknown>(
    `business/dashboard/attention/running-late/${encodeURIComponent(assignmentId)}/ack`,
    { method: "POST" },
  );
}
